import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useEffect, useState } from "react";
import { useGetLlmClients } from "../api/useGetLlmClients";
import { useEnableLlmClient } from "../api/useEnableLlmClient";
import { useDisableLlmClient } from "../api/useDisableLlmClient";
import { useDeleteLlmClient } from "../api/useDeleteLlmClient";
import {
  Plus,
  Trash2,
  GripVertical,
  Pencil,
  ArrowUpDown,
  Save,
} from "lucide-react";
import { LlmClientShort } from "../models/LlmClientShort";
import { AddEditClientModal } from "./AddEditClientModal";
import { AsyncToggle } from "@/shared/components/async-toggle";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useSortLlmClients } from "../api/useReorderLlmClients";

interface LlmClientsManagementProps {
  llmName: string;
  llmDisplayName: string;
}

interface SortableClientRowProps {
  client: LlmClientShort;
  onEdit: (client: LlmClientShort) => void;
  onDelete: (id: string) => void;
  isReorderMode: boolean;
  enableFunction: {
    isDone: boolean;
    call: (llmName: string, clientId: string) => Promise<void>;
    isFailed: boolean;
  };
  disableFunction: {
    isDone: boolean;
    call: (llmName: string, clientId: string) => Promise<void>;
    isFailed: boolean;
  };
  llmName: string;
}

function SortableClientRow({
  client,
  onEdit,
  onDelete,
  isReorderMode,
  enableFunction,
  disableFunction,
  llmName,
}: SortableClientRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: client.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <TableRow ref={setNodeRef} style={style} {...attributes}>
      <TableCell>
        <div className="flex items-center gap-2">
          {isReorderMode && (
            <div {...listeners} className="cursor-grab hover:cursor-grabbing">
              <GripVertical className="h-4 w-4 text-gray-400" />
            </div>
          )}
          <span className="font-medium">{client.clientName}</span>
        </div>
      </TableCell>
      <TableCell>
        <AsyncToggle
          initialState={client.isEnabled}
          enableFunction={{
            isDone: enableFunction.isDone,
            call: () => enableFunction.call(llmName, client.id),
            isFailed: enableFunction.isFailed,
          }}
          disableFunction={{
            isDone: disableFunction.isDone,
            call: () => disableFunction.call(llmName, client.id),
            isFailed: disableFunction.isFailed,
          }}
          disabled={isReorderMode}
        />
      </TableCell>
      <TableCell className="text-sm text-gray-500 max-w-xs truncate">
        {client.note || "-"}
      </TableCell>
      <TableCell>
        {!isReorderMode && (
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onEdit(client)}
              className="text-gray-600 hover:text-gray-700"
            >
              <Pencil className="h-4 w-4" />
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-red-600 hover:text-red-700"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete Client</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to delete client "{client.clientName}
                    "? This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => onDelete(client.id)}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        )}
      </TableCell>
    </TableRow>
  );
}

export function LlmClientsManagement({
  llmName,
  llmDisplayName,
}: LlmClientsManagementProps) {
  const { llmClients, getLlmClients, isLoading } = useGetLlmClients();
  const {
    enableLlmClient,
    isEnabled,
    isFailed: isEnablingFailed,
  } = useEnableLlmClient();
  const {
    disableLlmClient,
    isDisabled,
    isFailed: isDisablingFailed,
  } = useDisableLlmClient();
  const { deleteLlmClient, isDeleted } = useDeleteLlmClient();
  const { sortLlmClients } = useSortLlmClients();
  const [sortedClients, setSortedClients] = useState<LlmClientShort[]>([]);
  const [isReorderMode, setIsReorderMode] = useState(false);
  const [originalOrder, setOriginalOrder] = useState<LlmClientShort[]>([]);
  const [pendingDeleteClient, setPendingDeleteClient] = useState<string>();
  const [isClientModalOpen, setIsClientModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string>();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  useEffect(() => {
    getLlmClients(llmName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [llmName]);

  useEffect(() => {
    if (llmClients) {
      setSortedClients([...llmClients]);
    }
  }, [llmClients]);

  // Handle successful delete operation
  useEffect(() => {
    if (isDeleted && pendingDeleteClient) {
      setSortedClients((items) =>
        items.filter((item) => item.id !== pendingDeleteClient),
      );
      setPendingDeleteClient(undefined);
    }
  }, [isDeleted, pendingDeleteClient]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      setSortedClients((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over?.id);

        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleReorderStart = () => {
    setIsReorderMode(true);
    setOriginalOrder([...sortedClients]);
  };

  const handleReorderSave = async () => {
    const orderedClientIds = sortedClients.map((item) => item.id);
    await sortLlmClients(llmName, orderedClientIds);
    setIsReorderMode(false);
    setOriginalOrder([]);
  };

  const handleReorderCancel = () => {
    setSortedClients([...originalOrder]);
    setIsReorderMode(false);
    setOriginalOrder([]);
  };

  const handleEdit = (client: LlmClientShort) => {
    setEditingId(client.id);
    setIsClientModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    // Set pending client for tracking
    setPendingDeleteClient(id);

    await deleteLlmClient(llmName, id);
    // UI update will be handled by useEffect when isDeleted changes
  };

  const handleAddClient = () => {
    setEditingId(undefined);
    setIsClientModalOpen(true);
  };

  const handleClientModalClose = () => {
    setIsClientModalOpen(false);
    setEditingId(undefined);
    getLlmClients(llmName);
  };

  if (isLoading) {
    return (
      <div className="bg-gray-50 p-4 border-t">
        <div className="flex justify-center py-4">
          <div className="text-sm text-gray-500">Loading clients...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 p-4 border-t">
      <div className="flex justify-between items-center mb-4">
        <div className="text-sm font-medium text-gray-700">
          LLM Clients for {llmDisplayName}
        </div>
        <div className="flex items-center gap-2">
          {isReorderMode ? (
            <>
              <Button variant="outline" size="sm" onClick={handleReorderCancel}>
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleReorderSave}
                className="flex items-center gap-2"
              >
                <Save className="h-4 w-4" />
                Save Order
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={handleReorderStart}
                className="flex items-center gap-2"
              >
                <ArrowUpDown className="h-4 w-4" />
                Reorder
              </Button>
              <Button
                size="sm"
                onClick={handleAddClient}
                className="flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                Add Client
              </Button>
            </>
          )}
        </div>
      </div>

      {sortedClients && sortedClients.length > 0 ? (
        <DndContext
          sensors={isReorderMode ? sensors : []}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Enabled</TableHead>
                <TableHead>Note</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <SortableContext
                items={sortedClients.map((client) => client.id)}
                strategy={verticalListSortingStrategy}
              >
                {sortedClients.map((client) => (
                  <SortableClientRow
                    key={client.id}
                    client={client}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    isReorderMode={isReorderMode}
                    enableFunction={{
                      isDone: !!isEnabled,
                      call: enableLlmClient,
                      isFailed: !!isEnablingFailed,
                    }}
                    disableFunction={{
                      isDone: !!isDisabled,
                      call: disableLlmClient,
                      isFailed: !!isDisablingFailed,
                    }}
                    llmName={llmName}
                  />
                ))}
              </SortableContext>
            </TableBody>
          </Table>
        </DndContext>
      ) : (
        <div className="text-sm text-gray-500 text-center py-8 bg-white rounded border">
          No clients found for {llmDisplayName}
        </div>
      )}

      <AddEditClientModal
        open={isClientModalOpen}
        onClose={handleClientModalClose}
        llmName={llmName}
        id={editingId}
        onSuccess={() => getLlmClients(llmName)}
      />
    </div>
  );
}
